import React, { ComponentProps, useCallback, useEffect } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { useNonNullableContext } from "@mapmycustomers/shared/util/react";

import { fetchEmailTemplates } from "@app/store/email/actions";

import CreateEmailModalsContext from "./creation/CreateEmailModalsContext";
import EmailTemplatesModal from "./EmailTemplatesModal";
import EmailsTemplatesModalContext from "./EmailTemplatesModalContext";

type EmailTemplateModalProviderProps = typeof mapDispatchToProps;

const EmailTemplateModalProvider: React.FC<EmailTemplateModalProviderProps> = ({
  children,
  onFetchEmailTemplates,
}) => {
  const intl = useIntl();
  const { showEmailModal } = useNonNullableContext(CreateEmailModalsContext);

  const [emailTemplatesModalOpen, showEmailTemplatesModal, hideEmailTemplatesModal] = useBoolean();

  const handleApply: ComponentProps<typeof EmailTemplatesModal>["onApply"] = useCallback(
    (template) => {
      hideEmailTemplatesModal();
      showEmailModal({
        template,
      });
    },
    [hideEmailTemplatesModal, showEmailModal]
  );

  useEffect(() => {
    if (emailTemplatesModalOpen) {
      onFetchEmailTemplates();
    }
  }, [emailTemplatesModalOpen, onFetchEmailTemplates]);

  return (
    <EmailsTemplatesModalContext.Provider
      value={{ emailTemplatesModalOpen, hideEmailTemplatesModal, showEmailTemplatesModal }}
    >
      {children}
      {emailTemplatesModalOpen && (
        <EmailTemplatesModal
          applyLabel={intl.formatMessage({
            id: "email.templatesModal.composeWithTemplateLabel",
            defaultMessage: "Compose with template",
            description: "Email templates modal compose with templates label",
          })}
          onApply={handleApply}
          onHide={hideEmailTemplatesModal}
        />
      )}
    </EmailsTemplatesModalContext.Provider>
  );
};

const mapDispatchToProps = {
  onFetchEmailTemplates: fetchEmailTemplates.request,
};

export default connect(null, mapDispatchToProps)(EmailTemplateModalProvider);
